import type { Workspace } from '@shared/domain/permissions'
import {
  FinancingStatus,
  type AnchorStatus,
  type CollectionStatus,
  type InvoiceStatus,
  type ProgrammeStatus,
  type SupplierStatus,
} from './codes'

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export interface StatusMeta {
  /** i18n key, never a raw label */
  labelKey: string
  /** drives StatusChip colour */
  tone: Tone
  /** workflow stage 1–9 */
  stage: number
  visibleTo: Workspace[]
  /** no further asynchronous change is expected, so live polling stops */
  terminal?: boolean
  /** show a softer status to some workspaces */
  maskAs?: Partial<Record<Workspace, string>>
}

// Platform Ops sees every status. Anchor/Supplier visibility is the proposed default from §4;
// the bank must confirm the Stage 8–9 rules ("To confirm" rows default to hidden).
const ALL: Workspace[] = ['anchor', 'supplier', 'ops']
const ANCHOR_OPS: Workspace[] = ['anchor', 'ops']
const SUPPLIER_OPS: Workspace[] = ['supplier', 'ops']
const OPS: Workspace[] = ['ops']

export const programmeStatusMeta: Record<ProgrammeStatus, StatusMeta> = {
  AVAILABLE_FOR_ANCHOR_SETUP: {
    labelKey: 'status.programme.availableForAnchorSetup',
    tone: 'success',
    stage: 1,
    visibleTo: ANCHOR_OPS,
  },
  VALIDATION_FAILED: {
    labelKey: 'status.programme.validationFailed',
    tone: 'danger',
    stage: 1,
    visibleTo: OPS,
  },
}

export const anchorStatusMeta: Record<AnchorStatus, StatusMeta> = {
  DRAFT: { labelKey: 'status.anchor.draft', tone: 'neutral', stage: 2, visibleTo: ANCHOR_OPS },
  SUBMITTED: { labelKey: 'status.anchor.submitted', tone: 'info', stage: 2, visibleTo: ANCHOR_OPS },
  ANCHOR_STATUS_ACTIVE: {
    labelKey: 'status.anchor.active',
    tone: 'success',
    stage: 2,
    visibleTo: ANCHOR_OPS,
  },
  AVAILABLE_FOR_SUPPLIER_ONBOARDING: {
    labelKey: 'status.anchor.availableForSupplierOnboarding',
    tone: 'success',
    stage: 2,
    visibleTo: ANCHOR_OPS,
    terminal: true,
  },
  UNDER_SETUP: {
    labelKey: 'status.anchor.underSetup',
    tone: 'info',
    stage: 2,
    visibleTo: ANCHOR_OPS,
  },
  ACTIVE: { labelKey: 'status.anchor.active', tone: 'success', stage: 2, visibleTo: ANCHOR_OPS },
  SUSPENDED: {
    labelKey: 'status.anchor.suspended',
    tone: 'warning',
    stage: 2,
    visibleTo: ANCHOR_OPS,
  },
  CLOSED: {
    labelKey: 'status.anchor.closed',
    tone: 'neutral',
    stage: 2,
    visibleTo: ANCHOR_OPS,
    terminal: true,
  },
}

export const supplierStatusMeta: Record<SupplierStatus, StatusMeta> = {
  NOMINATED: { labelKey: 'status.supplier.nominated', tone: 'neutral', stage: 3, visibleTo: ALL },
  INVITED: { labelKey: 'status.supplier.invited', tone: 'neutral', stage: 3, visibleTo: ALL },
  REGISTRATION_IN_PROGRESS: {
    labelKey: 'status.supplier.registrationInProgress',
    tone: 'info',
    stage: 3,
    visibleTo: ALL,
  },
  PENDING_BANK_REVIEW: {
    labelKey: 'status.supplier.pendingBankReview',
    tone: 'info',
    stage: 3,
    visibleTo: ALL,
  },
  SUPPLIER_APPROVED_FOR_PROGRAMME: {
    labelKey: 'status.supplier.approved',
    tone: 'success',
    stage: 3,
    visibleTo: ALL,
    terminal: true,
  },
  SUPPLIER_REJECTED: {
    labelKey: 'status.supplier.rejected',
    tone: 'danger',
    stage: 3,
    visibleTo: ALL,
    terminal: true,
  },
  // Supplier master profile. Anchors see it too: GET /suppliers returns their linked suppliers.
  DRAFT: { labelKey: 'status.supplier.draft', tone: 'neutral', stage: 3, visibleTo: ALL },
  SUBMITTED: {
    labelKey: 'status.supplier.submitted',
    tone: 'info',
    stage: 3,
    visibleTo: ALL,
  },
  ACTIVE: {
    labelKey: 'status.supplier.active',
    tone: 'success',
    stage: 3,
    visibleTo: ALL,
  },
  SUSPENDED: {
    labelKey: 'status.supplier.suspended',
    tone: 'warning',
    stage: 3,
    visibleTo: ALL,
  },
  CLOSED: {
    labelKey: 'status.supplier.closed',
    tone: 'neutral',
    stage: 3,
    visibleTo: ALL,
    terminal: true,
  },
}

export const invoiceStatusMeta: Record<InvoiceStatus, StatusMeta> = {
  RECEIVED: {
    labelKey: 'status.invoice.received',
    tone: 'neutral',
    stage: 4,
    visibleTo: ANCHOR_OPS,
  },
  INVOICE_ELIGIBLE: {
    labelKey: 'status.invoice.eligible',
    tone: 'success',
    stage: 4,
    visibleTo: ALL,
  },
  INVOICE_INELIGIBLE: {
    labelKey: 'status.invoice.ineligible',
    tone: 'danger',
    stage: 4,
    visibleTo: ALL,
    terminal: true,
  },
  AVAILABLE_FOR_FINANCING_REQUEST: {
    labelKey: 'status.invoice.availableForFinancing',
    tone: 'info',
    stage: 4,
    visibleTo: ALL,
  },
  LOCKED_FOR_FINANCING: {
    labelKey: 'status.invoice.locked',
    tone: 'warning',
    stage: 5,
    visibleTo: ALL,
  },
}

export const financingStatusMeta: Record<FinancingStatus, StatusMeta> = {
  FINANCING_REQUEST: {
    labelKey: 'status.financing.submitted',
    tone: 'info',
    stage: 5,
    visibleTo: SUPPLIER_OPS,
  },
  PENDING_BANK_REVIEW: {
    labelKey: 'status.financing.inReview',
    tone: 'info',
    stage: 5,
    visibleTo: SUPPLIER_OPS,
  },
  REFERRED: {
    labelKey: 'status.financing.referred',
    tone: 'warning',
    stage: 5,
    visibleTo: SUPPLIER_OPS,
    maskAs: { supplier: FinancingStatus.PENDING_BANK_REVIEW },
  },
  APPROVED: {
    labelKey: 'status.financing.approved',
    tone: 'success',
    stage: 5,
    visibleTo: SUPPLIER_OPS,
  },
  CONDITIONALLY_APPROVED: {
    labelKey: 'status.financing.conditionallyApproved',
    tone: 'warning',
    stage: 6,
    visibleTo: SUPPLIER_OPS,
  },
  REJECTED: {
    labelKey: 'status.financing.rejected',
    tone: 'danger',
    stage: 5,
    visibleTo: SUPPLIER_OPS,
    terminal: true,
  },
  READY_FOR_BOOKING: {
    labelKey: 'status.financing.readyForBooking',
    tone: 'info',
    stage: 6,
    visibleTo: SUPPLIER_OPS,
  },
  BOOKED: { labelKey: 'status.financing.booked', tone: 'info', stage: 7, visibleTo: ALL },
  FINANCING_DISBURSED: {
    labelKey: 'status.financing.disbursed',
    tone: 'success',
    stage: 7,
    visibleTo: ALL,
  },
  BOOKING_FAILED: {
    labelKey: 'status.financing.bookingFailed',
    tone: 'danger',
    stage: 7,
    visibleTo: SUPPLIER_OPS,
  },
  DISBURSEMENT_FAILED: {
    labelKey: 'status.financing.disbursementFailed',
    tone: 'danger',
    stage: 7,
    visibleTo: SUPPLIER_OPS,
  },
  SETTLED: {
    labelKey: 'status.settlement.settled',
    tone: 'success',
    stage: 8,
    visibleTo: ALL,
    terminal: true,
  },
  PARTIALLY_SETTLED: {
    labelKey: 'status.settlement.partiallySettled',
    tone: 'warning',
    stage: 8,
    visibleTo: ALL,
  },
  OVERDUE: { labelKey: 'status.settlement.overdue', tone: 'danger', stage: 9, visibleTo: ALL },
  CURED: { labelKey: 'status.recovery.cured', tone: 'success', stage: 9, visibleTo: SUPPLIER_OPS },
  RESTRUCTURED: {
    labelKey: 'status.recovery.restructured',
    tone: 'warning',
    stage: 9,
    visibleTo: SUPPLIER_OPS,
  },
  DEFAULTED: {
    labelKey: 'status.recovery.defaulted',
    tone: 'danger',
    stage: 9,
    visibleTo: OPS,
    terminal: true,
  },
  WRITTEN_OFF: {
    labelKey: 'status.recovery.writtenOff',
    tone: 'danger',
    stage: 9,
    visibleTo: OPS,
    terminal: true,
  },
}

export const collectionStatusMeta: Record<CollectionStatus, StatusMeta> = {
  DUE: { labelKey: 'status.collection.due', tone: 'neutral', stage: 8, visibleTo: ALL },
  SETTLED: {
    labelKey: 'status.settlement.settled',
    tone: 'success',
    stage: 8,
    visibleTo: ALL,
    terminal: true,
  },
  PARTIALLY_SETTLED: {
    labelKey: 'status.settlement.partiallySettled',
    tone: 'warning',
    stage: 8,
    visibleTo: ALL,
  },
  OVERDUE: { labelKey: 'status.settlement.overdue', tone: 'danger', stage: 9, visibleTo: ALL },
}

export const statusDictionary = {
  programme: programmeStatusMeta,
  anchor: anchorStatusMeta,
  supplier: supplierStatusMeta,
  invoice: invoiceStatusMeta,
  financing: financingStatusMeta,
  collection: collectionStatusMeta,
} as const satisfies Record<string, Record<string, StatusMeta>>

export type StatusEntity = keyof typeof statusDictionary
